import {
  Body1Strong,
  Caption1,
  Card,
  Link as FluentLink,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';

import { DateLabel } from '@shared/components';
import { FileIcon, useItemTableStyles, useVersionInfo } from '@modules/items';

import { getDefaultDueDate } from '../helpers';
import useResolvedTraining from '../hooks/useResolvedTraining';
import useTrainingStore from '../stores/useTrainingStore';
import TrainingStatusBadge from './TrainingStatusBadge';

// The "My training" home tab. It deliberately links into the document instead of
// exposing an action in the table; the document overview owns the confirmation.
const MyTrainingTable = () => {
  const tableClasses = useItemTableStyles();
  const training = useResolvedTraining();
  const isTrainingActive = useTrainingStore((state) =>
    training ? state.activeDocumentIds.includes(training.documentId) : false
  );
  const assignment = useTrainingStore((state) => state.assignment);
  const completion = useTrainingStore((state) => state.completion);
  const { data: versionInfo } = useVersionInfo(training?.documentId, training ? 'DOCUMENT' : undefined);

  if (!training || !isTrainingActive) {
    return <p>No training assigned.</p>;
  }

  const status = completion ? 'completed' : 'open';
  const dueDate = assignment?.dueDate ?? getDefaultDueDate(training.dueInDays);
  const lastApproved = versionInfo?.approvals.reduce<string | undefined>((latest, approval) => {
    if (approval.status !== 'APPROVED' || !approval.dateCompleted) return latest;
    if (!latest || new Date(approval.dateCompleted).getTime() > new Date(latest).getTime()) {
      return approval.dateCompleted;
    }
    return latest;
  }, undefined);

  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '280px' }}>
              <Body1Strong>Name</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '150px' }}>
              <Body1Strong>Reason</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '150px' }}>
              <Body1Strong>Method</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '110px' }}>
              <Body1Strong>Due date</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '100px' }}>
              <Body1Strong>Status</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '110px' }}>
              <Body1Strong>Last approved</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody className={tableClasses.tableBody}>
          <TableRow>
            <TableCell>
              <TableCellLayout
                media={<FileIcon type="DOCUMENT" path={[]} isBlock={false} isFrozen={false} isTemplate={false} />}
                description={<Caption1 className="text-neutralFg-3">{training.revision}</Caption1>}
              >
                <Link to={`/document/${training.documentId}`} target="_blank">
                  <FluentLink as="span" appearance="subtle">
                    {training.documentName}
                  </FluentLink>
                </Link>
              </TableCellLayout>
            </TableCell>

            <TableCell>{training.reason}</TableCell>
            <TableCell>{training.trainingMethod}</TableCell>
            <TableCell>
              <DateLabel short>{dueDate}</DateLabel>
            </TableCell>
            <TableCell>
              <TrainingStatusBadge status={status} />
            </TableCell>
            <TableCell>{lastApproved ? <DateLabel short>{lastApproved}</DateLabel> : '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
};

export default MyTrainingTable;
